import React, {ChangeEvent, memo, useMemo, useRef, useState} from 'react';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import {Box, Typography} from '@mui/material';
import {ReactComponent as AiIconHover} from '../../../../../../assets/images/icon-ai-hover.svg';
import {useUploadProtocolFileV2} from '../../../../../../hooks/ai/review/useReview';
import {useQueryClient} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import {
  getProtocolNameValidationError,
  ProtocolNameValidationError,
} from '../../../common/JobDetails/SaveAsNewProtocolModal';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import {getProtocolNameValidationMessage} from '../../../common/protocolIntelligenceV2/create-protocol-modal/CreateNewProtocolModal';
import {clearProtocolUploadedContent} from '../../../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../../../redux/store';
import {getProtocolFileDisplayName} from '../../utils/protocolVersionPayload';

interface IUploadProtocol {
  handleUploadProtocol: (
    isUploading: boolean,
    protocolId?: number | null,
    fileName?: string | null
  ) => void;
  isBtnDisabled: boolean;
}
const ACCEPTED_FILE_TYPES = '.doc, .docx, .pdf';
const FILE_SIZE_LIMIT_MB = 500 * 1024 * 1000;
const allowedFileTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
];
const MAX_FILE_NAME_CHAR_LENGTH = 50;

type TYPE_MODAL_DETAILS_LIST = {
  [key: string]: {
    title: string;
    description: string;
  };
};

const MODAL_DETAILS_LIST: TYPE_MODAL_DETAILS_LIST = {
  LARGE_FILE: {
    title: t('ai.review.protocolIntelligence.uploadErrorModalFileSizeTitle'),
    description: t(
      'ai.review.protocolIntelligence.uploadErrorModalFileSizeDescription'
    ),
  },
  UPLOAD_FAILED: {
    title: t('ai.review.protocolIntelligence.uploadErrorModalTitle'),
    description: t(
      'ai.review.protocolIntelligence.uploadErrorModalDescription'
    ),
  },
  FILE_TYPE: {
    title: t('ai.review.protocolIntelligence.uploadErrorModalTitle'),
    description: t(
      'ai.review.protocolIntelligence.uploadErrorModalDescription'
    ),
  },
  FILE_NAME_CHAR_LEN: {
    title: t('ai.review.protocolIntelligence.protocolFileNameModalTitle'),
    description: '',
  },
  CLEAR_CONTENT: {
    title: t('ai.review.protocolIntelligence.clearProtocolTitle'),
    description: t('ai.review.protocolIntelligence.clearProtocolDescription'),
  },
};

const UploadProtocol = ({
  handleUploadProtocol,
  isBtnDisabled,
}: IUploadProtocol) => {
  const [errorModalDetails, setErrorModalDetails] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [clearContentModal, setClearContentModal] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [fileNameError, setFileNameError] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [customFileName, setCustomFileName] = useState('');
  const [
    protocolNameValidationError,
    setProtocolNameValidationValidationError,
  ] = useState<ProtocolNameValidationError | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const {mutate: uploadProtocolFile} = useUploadProtocolFileV2();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const fileProtocolId = useRef<number>(null);
  const serverFileName = useSelector((state: RootState) =>
    getProtocolFileDisplayName(state.aiReviewV2.protocol.fileDetails?.fileName)
  );
  const hasUploadedFile = Boolean(serverFileName);

  const handleInputFile = () => {
    if (!hasUploadedFile) {
      fileRef.current?.click();
    } else {
      setClearContentModal(MODAL_DETAILS_LIST.CLEAR_CONTENT);
    }
  };

  const handleFileUpload = (file: File) => {
    handleUploadProtocol(true);
    uploadProtocolFile(
      {file},
      {
        onSuccess: (data) => {
          const resolvedFileName =
            data?.fileName?.trim() || file.name?.trim() || '';
          handleUploadProtocol(
            true,
            data?.protocolArtifactId,
            resolvedFileName
          );
          setErrorModalDetails(null);
          setFileNameError(false);
          setCustomFileName('');
          fileProtocolId.current = data?.protocolArtifactId;
        },
        onError: () => {
          setErrorModalDetails(MODAL_DETAILS_LIST.UPLOAD_FAILED);
          handleUploadProtocol(false);
        },
      }
    );
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file?.size > FILE_SIZE_LIMIT_MB) {
      setErrorModalDetails(MODAL_DETAILS_LIST.LARGE_FILE);
      return;
    }
    if (
      allowedFileTypes.length > 0 &&
      !allowedFileTypes.includes(file?.type ?? '')
    ) {
      setErrorModalDetails(MODAL_DETAILS_LIST.FILE_TYPE);
      return;
    }
    if (file?.name?.length && file?.name?.length > MAX_FILE_NAME_CHAR_LENGTH) {
      setPendingFile(file);
      setFileNameError(true);
      return;
    }
    file && handleFileUpload(file);
  };

  const handleClearContent = () => {
    queryClient.setQueryData(
      ['getProtocolIntelligenceV2', fileProtocolId.current],
      null
    );
    dispatch(clearProtocolUploadedContent());
    handleUploadProtocol(false, null, '');
    setClearContentModal(null);
  };
  const handleSaveAndUpload = () => {
    if (!pendingFile) return;
    const extension = pendingFile.name.includes('.')
      ? `.${pendingFile.name.split('.').pop()}`
      : '';
    const renamedFile = new File(
      [pendingFile],
      `${customFileName}${extension}`,
      {
        type: pendingFile.type,
        lastModified: pendingFile.lastModified,
      }
    );
    handleFileUpload(renamedFile);
  };

  const handleNewFileNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value = ''} = event.target;
    if (value.length <= MAX_FILE_NAME_CHAR_LENGTH) {
      const error = getProtocolNameValidationError(value, [], true);
      setProtocolNameValidationValidationError(error);
      setCustomFileName(value);
    }
  };

  const helperText = useMemo(() => {
    const errorMessage = getProtocolNameValidationMessage(
      protocolNameValidationError
    );
    if (errorMessage) {
      return errorMessage;
    }
    return `${customFileName.length}/${MAX_FILE_NAME_CHAR_LENGTH}`;
  }, [customFileName.length, protocolNameValidationError]);

  return (
    <Box className="upload-protocol-root">
      <Box
        className="upload-banner"
        role="region"
        aria-label={
          hasUploadedFile
            ? t('ai.review.protocolIntelligence.uploadedProtocol')
            : t('ai.review.protocolIntelligence.uploadProtocolBannerLabel')
        }
      >
        <Box className="upload-banner-content">
          <AiIconHover
            data-testid="ai-icon-hover"
            className="icon-navbar"
            aria-hidden="true"
          />
          {hasUploadedFile ? (
            <Box className="protocol-details">
              <Typography className="typo-text body3_size text-body-subtle">
                {t('ai.review.protocolIntelligence.uploadedProtocol')}{' '}
              </Typography>
              <Typography
                className="typo-text subhead5_size"
                data-testid="uploaded-protocol-file-name"
              >
                {serverFileName}
              </Typography>
            </Box>
          ) : (
            <Typography className="typo-text body3_size">
              {t('ai.review.protocolIntelligence.uploadProtocolDesc')}
            </Typography>
          )}
        </Box>
        <ArrButton
          variant="primary"
          size="small"
          dataTestId="upload-protocol"
          onClick={handleInputFile}
          disabled={isBtnDisabled}
          aria-label={
            serverFileName
              ? t('common.clear')
              : t('ai.review.protocolIntelligence.uploadProtocolV2')
          }
        >
          {serverFileName
            ? t('common.clear')
            : t('ai.review.protocolIntelligence.uploadProtocolV2')}
        </ArrButton>
      </Box>
      <input
        hidden
        type="file"
        ref={fileRef}
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileChange}
        aria-label={t(
          'ai.review.protocolIntelligence.uploadProtocolFileLabel'
        )}
      />
      {errorModalDetails && (
        <ArrModal
          showClose={false}
          showModal={!!errorModalDetails}
          onClose={() => setErrorModalDetails(null)}
          title={errorModalDetails?.title}
          aria-label={errorModalDetails?.title}
          dataTestId="arr-upload-protocol-error-modal"
          buttons={[
            {
              id: t('common.close'),
              label: t('common.close'),
              onClick: () => setErrorModalDetails(null),
              variant: 'secondary',
              dataTestId: 'modal-no',
            },
            {
              id: t('common.tryAgain'),
              label: t('common.tryAgain'),
              onClick: handleInputFile,
              variant: 'primary',
              dataTestId: 'modal-yes',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            data-testid={`upload-protocol-error-description`}
          >
            {errorModalDetails.description}
          </Typography>
        </ArrModal>
      )}
      {clearContentModal && (
        <ArrModal
          className="clear-protocol-content-modal"
          showClose={false}
          showModal={!!clearContentModal}
          onClose={() => setClearContentModal(null)}
          title={clearContentModal?.title}
          aria-label={clearContentModal?.title}
          dataTestId="arr-clear-protocol-modal"
          buttons={[
            {
              id: t('common.cancel'),
              label: t('common.cancel'),
              onClick: () => setClearContentModal(null),
              variant: 'secondary',
              dataTestId: 'modal-no',
            },
            {
              id: t('ai.review.protocolIntelligence.clearContent'),
              label: t('ai.review.protocolIntelligence.clearContent'),
              onClick: handleClearContent,
              variant: 'primary',
              dataTestId: 'modal-yes',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            data-testid={`upload-protocol-error-description`}
          >
            {clearContentModal.description}
          </Typography>
        </ArrModal>
      )}
      {fileNameError && (
        <ArrModal
          title={t('ai.review.protocolIntelligence.protocolFileNameModalTitle')}
          showModal={fileNameError}
          onClose={() => setFileNameError(false)}
          showClose={false}
          dataTestId="failed-fileName-protocol-modal"
          buttons={[
            {
              id: t('common.close'),
              label: t('common.close'),
              onClick: () => setFileNameError(false),
              dataTestId: 'fn-modal-no',
              variant: 'secondary',
            },
            {
              id: t('ai.review.protocolIntelligence.saveAndUpload'),
              label: t('ai.review.protocolIntelligence.saveAndUpload'),
              onClick: handleSaveAndUpload,
              variant: 'primary',
              dataTestId: 'fn-modal-yes',
              disabled:
                Boolean(protocolNameValidationError) ||
                Boolean(customFileName.length === 0),
            },
          ]}
          className="delete-protocol-modal"
        >
          <Box className="delete-protocol-modal-description">
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p1"
            >
              {t(
                'ai.review.protocolIntelligence.protocolFileNameModalDescription'
              )}
            </Typography>
          </Box>
          <Box>
            <ArrTextField
              label={t(
                'ai.review.protocolIntelligence.protocolFileNameModalInputLabel'
              )}
              value={customFileName}
              error={Boolean(protocolNameValidationError)}
              state={protocolNameValidationError ? 'error' : 'enabled'}
              showErrorIcon={Boolean(protocolNameValidationError)}
              onChange={handleNewFileNameChange}
              dataTestId="new-file-name-input"
              className="mt-16"
              supportText={helperText}
              helperText={helperText}
              slotProps={{
                input: {
                  inputProps: {maxLength: MAX_FILE_NAME_CHAR_LENGTH},
                },
                formHelperText: {
                  'aria-label': protocolNameValidationError
                    ? getProtocolNameValidationMessage(
                        protocolNameValidationError
                      )
                    : `${customFileName.length} ${t('ai.review.jobDetailsTab.saveAsNewProtocolOutOf50Characters')}`,
                },
              }}
            />
          </Box>
        </ArrModal>
      )}
    </Box>
  );
};

export default memo(UploadProtocol);
